import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import countries from 'world-atlas/countries-110m.json' with { type: 'json' };

/**
 * World-map geometry for the "Available in your language" use case.
 *
 * The map is rendered once per locale as static SVG during the site's SSR
 * prerender. Supported regions are shaded green; everything else is a light
 * outline. Two orientations are produced by rotating the Natural Earth
 * projection: the Atlantic-centred view (Americas on the left, used for
 * English) and the Pacific-centred view (Americas on the right, used for the
 * Asian locales), matching the reader's mental model of the world. The 110m
 * world-atlas dataset keeps the client bundle lean (the 50m file is ~7x
 * larger with no visible gain at world-map scale); the smallest supported
 * territories are pinned instead.
 */

/** @type {number} — fit-width used to size the projection before clipping. */
const MAP_WIDTH = 980;

/**
 * world-atlas country names where a Thyme-supported language is clearly
 * spoken. The rule from the issue: a country is shaded if its language is a
 * Thyme language; where a country has several main languages, shade it only
 * if both of its top two are supported (Singapore yes — English + Chinese;
 * India no — Hindi isn't shipped; Malaysia no — Malay isn't shipped).
 * Taiwan is shaded as a supported region (Traditional Chinese is spoken
 * there); Hong Kong and Macao are not pinned because they are part of the
 * already-shaded China shape.
 * @type {ReadonlySet<string>}
 */
const SUPPORTED_COUNTRIES = new Set([
	// English
	'United States of America', 'United Kingdom', 'Ireland', 'Canada',
	'Australia', 'New Zealand', 'South Africa', 'Jamaica',
	'Trinidad and Tobago', 'Bahamas', 'Barbados', 'Guyana', 'Belize',
	'Fiji', 'Papua New Guinea',
	// Japanese
	'Japan',
	// Korean
	'South Korea',
	// Simplified Chinese
	'China', 'Singapore',
	// Traditional Chinese
	'Taiwan',
]);

/**
 * Supported territories that have no shape at 110m resolution — pin them so
 * Singapore and Barbados stay obvious on the map.
 * @type {Array<[string, number, number]>} — [name, longitude, latitude]
 */
const PINS = [
	['Singapore', 103.8198, 1.3521],
	['Barbados', -59.5432, 13.1939],
];

/** @type {import('topojson-specification').Topology} — world-atlas data. */
const TOPOLOGY = /** @type {*} */ (countries);

/**
 * Decoded once at module load. Properties are cast so the null-able GeoJSON
 * `properties` (always present in world-atlas) doesn't leak into the map.
 * @type {import('geojson').FeatureCollection}
 */
const LAND = /** @type {import('geojson').FeatureCollection} */ (
	/** @type {unknown} */ (
		feature(TOPOLOGY, /** @type {import('topojson-specification').GeometryObject} */ (/** @type {*} */ (TOPOLOGY.objects.countries)))
	)
);

/**
 * Projection rotation per locale. `en` keeps the Atlantic centred (Americas
 * left); the Asian locales centre the Pacific (Americas right).
 * @param {string} lang — canonical locale code.
 * @returns {[number, number]}
 */
function getRotation(lang) {
	if (lang === 'ja' || lang === 'ko' || lang === 'zh-CN' || lang === 'zh-TW') {
		return [-150, 0];
	}
	return [0, 0];
}

/**
 * @typedef {Object} WorldMapCountry
 * @property {string} name — world-atlas country name (shown in the tooltip).
 * @property {string} d — SVG path data for the country outline.
 * @property {boolean} supported — whether a Thyme language is spoken there.
 */

/**
 * @typedef {Object} WorldMapPin
 * @property {string} name
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} WorldMapResult
 * @property {string} viewBox — `x y width height` for the SVG.
 * @property {number} width
 * @property {number} height
 * @property {Array<WorldMapCountry>} countries
 * @property {Array<WorldMapPin>} pins
 */

/**
 * Compute the rendered world map for a locale. Deterministic — the same input
 * always yields the same SVG, so the prerendered HTML matches client
 * hydration exactly.
 * @param {string} lang — canonical locale code (en, ja, ko, zh-CN, zh-TW).
 * @returns {WorldMapResult}
 */
export function buildWorldMap(lang) {
	const projection = geoNaturalEarth1()
		.rotate(getRotation(lang))
		.fitWidth(MAP_WIDTH, LAND);
	const path = geoPath(projection);
	const [[x0, y0], [x1, y1]] = path.bounds(LAND);

	return {
		viewBox: `${x0} ${y0} ${x1 - x0} ${y1 - y0}`,
		width: x1 - x0,
		height: y1 - y0,
		countries: LAND.features.map((f) => ({
			name: f.properties?.name ?? '',
			d: path(f) ?? '',
			supported: SUPPORTED_COUNTRIES.has(f.properties?.name ?? ''),
		})),
		pins: PINS.map(([name, lon, lat]) => {
			const [x, y] = projection([lon, lat]) ?? [0, 0];
			return { name, x, y };
		}),
	};
}
